import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import { Col, DataTable, FieldDef, FormModal, Notice, Pager, useAction, useList, Link2 } from './kit';

export interface PanelProps {
  qkey: string;
  title?: string;
  url: string;
  params?: Record<string, unknown>;
  cols: Col<any>[];
  /** Create form */
  createFields?: FieldDef[];
  createLabel?: string;
  createUrl?: string;
  createInitial?: Record<string, any>;
  mapCreate?: (v: Record<string, any>) => unknown;
  /** Edit (PUT) */
  editFields?: FieldDef[];
  editUrl?: (r: any) => string;
  mapEdit?: (v: Record<string, any>, r: any) => unknown;
  /** Delete */
  deleteUrl?: (r: any) => string;
  rowActions?: (r: any, ctx: { act: ReturnType<typeof useAction>['run']; refresh: () => void }) => ReactNode;
  toolbar?: ReactNode;
  empty?: string;
  enabled?: boolean;
  onRow?: (r: any) => void;
  /** Backend list is a plain array (no pagination) */
  plain?: boolean;
  extraAction?: ReactNode;
}

/** Declarative list + create/edit/delete panel for envelope endpoints. */
export function ResourcePanel(p: PanelProps) {
  const qc = useQueryClient();
  const list = useList(p.qkey, p.url, p.params, { enabled: p.enabled });
  const refresh = () => qc.invalidateQueries({ queryKey: [p.qkey] });
  const a = useAction(refresh);
  const [form, setForm] = useState<null | { row?: any }>(null);

  const cols: Col<any>[] = [...p.cols];
  if (p.editFields || p.deleteUrl || p.rowActions) {
    cols.push({
      head: 'Actions',
      cell: (r) => (
        <span className="flex gap-3 flex-wrap">
          {p.rowActions?.(r, { act: a.run, refresh })}
          {p.editFields && p.editUrl && <Link2 onClick={() => setForm({ row: r })}>Edit</Link2>}
          {p.deleteUrl && (
            <button type="button" className="text-red-600 text-sm hover:underline" onClick={(e) => { e.stopPropagation(); a.run(() => call('delete', p.deleteUrl!(r)), { confirm: 'Delete this record?' }); }}>
              Delete
            </button>
          )}
        </span>
      ),
    });
  }

  return (
    <Card
      title={p.title}
      action={
        <span className="flex gap-2">
          {p.extraAction}
          {p.createFields && <Button onClick={() => setForm({})}>{p.createLabel ?? 'Add'}</Button>}
        </span>
      }
    >
      {p.toolbar}
      {a.banner}
      <DataTable cols={cols} rows={list.rows} loading={list.isLoading} error={list.error} empty={p.empty} onRow={p.onRow} />
      {!p.plain && <Pager meta={list.meta} page={list.page} onPage={list.setPage} />}
      {form && !form.row && p.createFields && (
        <FormModal
          title={p.createLabel ?? 'Add'}
          fields={p.createFields}
          initial={p.createInitial}
          onClose={() => setForm(null)}
          onSubmit={async (v) => {
            await call('post', p.createUrl ?? p.url, { body: p.mapCreate ? p.mapCreate(v) : v });
            refresh();
          }}
        />
      )}
      {form?.row && p.editFields && p.editUrl && (
        <FormModal
          title="Edit"
          fields={p.editFields}
          initial={form.row}
          onClose={() => setForm(null)}
          onSubmit={async (v) => {
            await call('put', p.editUrl!(form.row), { body: p.mapEdit ? p.mapEdit(v, form.row) : v });
            refresh();
          }}
        />
      )}
    </Card>
  );
}

export function NA({ children }: { children?: ReactNode }) {
  return <Notice>{children ?? 'This feature is not available yet on the server.'}</Notice>;
}
